import { ContractProperty, ContractPropertyTreeNode, ContractPropertyScope } from '../types/contract';
import { v4 as uuidv4 } from 'uuid';

/**
 * Creates a new ContractProperty with default values.
 * @param scope The scope of the new property ('global' or 'eventSpecific').
 * @param parentProperty Optional. The parent property if creating a sub-property.
 * @returns A new ContractProperty object.
 */
export function createNewContractProperty(
  scope: ContractPropertyScope,
  parentProperty: ContractProperty | null = null
): ContractProperty {
  const id = uuidv4();
  const defaultCodeName = 'new_property';
  let path = defaultCodeName;
  let parentPaths: string[] = [];
  let level = 0;

  if (parentProperty) {
    parentPaths = parentProperty.parentPaths ? [...parentProperty.parentPaths, parentProperty.codeName] : [parentProperty.codeName];
    path = `${parentProperty.path}.${defaultCodeName}`;
    level = parentProperty.level !== undefined ? parentProperty.level + 1 : 1;
  }

  return {
    id,
    name: defaultCodeName.replace(/_/g, ' '), // Friendly name
    codeName: defaultCodeName,
    path,
    dataType: 'string', // Default data type
    description: '',
    isRequired: false,
    isPII: false,
    isNormalized: false,
    friendlyName: defaultCodeName.replace(/_/g, ' '),
    parentPaths,
    level,
    scope,
    isNew: true, // Mark as new for UI purposes
  };
}

/**
 * Builds a hierarchical tree structure from a flat list of ContractProperty objects.
 * Properties are nested under their parents using the 'children' property, inferred from their paths.
 * @param flatProperties A flat array of ContractProperty objects.
 * @param expansionState A map or object indicating which nodes should be expanded.
 * @returns A tree structure of ContractPropertyTreeNode objects.
 */
export function buildContractPropertyTree(
  flatProperties: ContractProperty[],
  expansionState: Record<string, boolean> = {}
): ContractPropertyTreeNode[] {
  const propertyMap = new Map<string, ContractPropertyTreeNode>();
  const rootProperties: ContractPropertyTreeNode[] = [];

  // First pass: Initialize map with cloned properties and add tree-specific fields
  flatProperties.forEach(prop => {
    const pathParts = prop.path.split('.');
    const codeName = pathParts[pathParts.length - 1];
    const parentPaths = pathParts.slice(0, -1);
    const level = parentPaths.length;

    const clonedProp: ContractPropertyTreeNode = {
      ...prop,
      codeName, // Ensure codeName is set
      parentPaths, // Ensure parentPaths is set
      level, // Ensure level is set
      children: [],
      isExpanded: expansionState[prop.path] ?? true, // Use path for expansion state
      isLastChild: false,
      hasChildren: false, // Will be updated later
    };
    propertyMap.set(clonedProp.path, clonedProp);
  });

  // Second pass: Build the tree and set levels
  propertyMap.forEach(prop => {
    if (prop.parentPaths && prop.parentPaths.length > 0) {
      const parentPath = prop.parentPaths.join('.');
      const parent = propertyMap.get(parentPath);
      if (parent) {
        parent.children!.push(prop);
        parent.hasChildren = true;
      } else {
        // If parent not found in map, it's a root property (e.g., "event.property" where "event" isn't a property itself)
        rootProperties.push(prop);
      }
    } else {
      rootProperties.push(prop);
    }
  });

  // Sort children and set isLastChild for consistent output and rendering
  propertyMap.forEach(prop => {
    if (prop.children) {
      prop.children.sort((a, b) => a.path.localeCompare(b.path));
      prop.children.forEach((child, index) => {
        child.isLastChild = index === prop.children!.length - 1;
      });
    }
  });

  // Filter out non-root properties from the main list and sort roots
  const finalTree = rootProperties.filter(prop => prop.level === 0);
  finalTree.sort((a, b) => a.path.localeCompare(b.path));

  return finalTree;
}

/**
 * Recursively flattens a tree of ContractPropertyTreeNode back into a flat array.
 */
export function flattenContractPropertyTree(tree: ContractPropertyTreeNode[]): ContractProperty[] {
  const flatList: ContractProperty[] = [];

  function traverse(nodes: ContractPropertyTreeNode[]) {
    nodes.forEach(node => {
      // Destructure to exclude tree-specific properties for the flat list
      const { children, isExpanded, level, isLastChild, hasChildren, ...rest } = node;
      flatList.push(rest);
      if (children && children.length > 0) {
        traverse(children);
      }
    });
  }

  traverse(tree);
  return flatList;
}

/**
 * Updates a property in a ContractPropertyTreeNode tree.
 * This function also handles path/codeName/parentPaths updates for the property and its children.
 * @param tree The array of ContractPropertyTreeNode to update.
 * @param propertyId The ID of the property to update.
 * @param updates The partial updates to apply to the property.
 * @returns A new tree with the updated property.
 */
export function updateContractPropertyInTree(
  tree: ContractPropertyTreeNode[],
  propertyId: string,
  updates: Partial<ContractProperty>
): ContractPropertyTreeNode[] {
  return tree.map(node => {
    if (node.id === propertyId) {
      const oldPath = node.path;
      let updatedNode: ContractPropertyTreeNode = { ...node, ...updates } as ContractPropertyTreeNode; // Cast to ensure type

      // Handle path/codeName changes and propagate to children
      if (updates.path !== undefined && updates.path !== oldPath) {
        const newPath = updates.path;
        const newPathSegments = newPath.split('.');
        const newCodeName = newPathSegments[newPathSegments.length - 1];
        const newParentPaths = newPathSegments.slice(0, -1);
        const newLevel = newParentPaths.length;

        updatedNode = {
          ...updatedNode,
          codeName: newCodeName,
          path: newPath,
          parentPaths: newParentPaths,
          level: newLevel,
        };
      } else if (updates.codeName !== undefined && updates.codeName !== node.codeName) {
        // If only codeName changed, reconstruct path and fullPath
        const newCodeName = updates.codeName;
        const parentPath = node.parentPaths && node.parentPaths.length > 0
          ? node.parentPaths.join('.')
          : '';
        const newPath = parentPath ? `${parentPath}.${newCodeName}` : newCodeName;

        updatedNode = {
          ...updatedNode,
          codeName: newCodeName,
          path: newPath,
        };
      }

      // Recursively update children's paths if the parent's path changed
      if (updatedNode.path !== oldPath && updatedNode.children && updatedNode.children.length > 0) {
        updatedNode.children = updatedNode.children.map(child => {
          const relativePath = child.path.substring(oldPath.length + 1);
          const newChildPath = `${updatedNode.path}.${relativePath}`;
          const newChildPathSegments = newChildPath.split('.');
          return {
            ...child,
            path: newChildPath,
            parentPaths: newChildPathSegments.slice(0, -1),
            level: newChildPathSegments.length - 1,
            children: child.children ? updateContractPropertyInTree(child.children, '', { path: newChildPath }) : [], // Recursively update children's children
          } as ContractPropertyTreeNode; // Cast to ensure type
        });
      }

      return updatedNode;
    }
    if (node.children && node.children.length > 0) {
      return {
        ...node,
        children: updateContractPropertyInTree(node.children, propertyId, updates),
      };
    }
    return node;
  });
}

/**
 * Deletes a property and all its children from a ContractPropertyTreeNode tree.
 * @param tree The array of ContractPropertyTreeNode to delete from.
 * @param propertyId The ID of the property to delete.
 * @returns A new tree with the property and its children removed.
 */
export function deleteContractPropertyFromTree(
  tree: ContractPropertyTreeNode[],
  propertyId: string
): ContractPropertyTreeNode[] {
  return tree.filter(node => node.id !== propertyId).map(node => {
    if (node.children && node.children.length > 0) {
      return {
        ...node,
        children: deleteContractPropertyFromTree(node.children, propertyId),
      };
    }
    return node;
  });
}

/**
 * Toggles the expanded state of a node in the ContractPropertyTreeNode tree.
 * @param tree The array of ContractPropertyTreeNode to update.
 * @param propertyId The ID of the node to toggle.
 * @returns A new tree with the expanded state toggled.
 */
export function toggleContractPropertyExpand(
  tree: ContractPropertyTreeNode[],
  propertyId: string
): ContractPropertyTreeNode[] {
  return tree.map(node => {
    if (node.id === propertyId) {
      return { ...node, isExpanded: !node.isExpanded };
    }
    if (node.children && node.children.length > 0) {
      return {
        ...node,
        children: toggleContractPropertyExpand(node.children, propertyId),
      };
    }
    return node;
  });
}
